import { Link } from 'react-router';
import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: React.ReactNode[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th
                key={i}
                className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };
  return (
    <div className={`my-4 p-4 rounded-xl border-l-4 ${styles[type]}`}>
      <div className="font-semibold text-sm mb-1">{labels[type]}</div>
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return <code className="text-xs font-mono text-primary-600 dark:text-primary-400">{children}</code>;
}

export function McpServerPage() {
  useDocumentMeta({
    title: 'MCP Server: connect ChatGPT, Claude, Cursor & Codex | LogPulse Docs',
    description:
      "Connect ChatGPT, Claude and coding agents to LogPulse over the Model Context Protocol. Endpoint, authentication, scopes, the full typed tool catalog, resources & prompts, and the security model for the LogPulse MCP server.",
  });

  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'endpoint', label: 'Endpoint & transport' },
    { id: 'authentication', label: 'Authentication' },
    { id: 'auth-pat', label: 'Personal access token', level: 1 },
    { id: 'auth-oauth', label: 'OAuth 2.1', level: 1 },
    { id: 'auth-organizations', label: 'Several organizations', level: 1 },
    { id: 'scopes', label: 'Scopes' },
    { id: 'connecting', label: 'Connecting an agent' },
    { id: 'connect-chatgpt', label: 'ChatGPT', level: 1 },
    { id: 'connect-claude-ai', label: 'Claude (web, desktop, mobile)', level: 1 },
    { id: 'connect-claude', label: 'Claude Code', level: 1 },
    { id: 'connect-cursor', label: 'Cursor', level: 1 },
    { id: 'connect-codex', label: 'Codex & other clients', level: 1 },
    { id: 'reconnect', label: 'Reconnecting an agent', level: 1 },
    { id: 'example-prompts', label: 'Example prompts' },
    { id: 'tools', label: 'Tool catalog' },
    { id: 'resources-prompts', label: 'Resources & prompts' },
    { id: 'security', label: 'Security model' },
    { id: 'data-privacy', label: 'Data & privacy' },
    { id: 'limits', label: 'Rate limits & auditing' },
    { id: 'troubleshooting', label: 'Troubleshooting' },
  ];

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        <Section id="overview" title="MCP Server">
          <P>
            The LogPulse MCP server is a remote{' '}
            <a
              href="https://modelcontextprotocol.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              Model Context Protocol
            </a>{' '}
            server. It lets AI assistants and agents, ChatGPT, Claude, Claude Code, Cursor, Codex, or any MCP-capable client, call
            typed LogPulse tools directly: search and investigate logs, query the risk-based SIEM,
            check service health, and walk the cross-domain entity graph. The agent reasons over
            structured results instead of scraping the dashboard or writing raw SQL.
          </P>
          <P>
            The tools reuse the same in-process registry, LPQL engine, and namespace RBAC the product
            uses internally, so an agent never gets more access than the token holder. The AI itself
            never changes your configuration: it reads, and it proposes changes that a person approves
            in LogPulse. In ChatGPT and Claude, results render as interactive widgets (charts, notables,
            service maps, proposals) instead of raw JSON.
          </P>
          <Callout type="info">
            ChatGPT and Claude connect with OAuth: you sign in to LogPulse and choose the
            organizations and permissions on a consent screen. Coding agents and automation can also
            use a{' '}
            <Link to="/docs/api/personal-access-tokens" className="underline">
              Personal Access Token
            </Link>
            .
          </Callout>
        </Section>

        <Section id="endpoint" title="Endpoint & transport">
          <P>
            The server is exposed at a single endpoint over Streamable HTTP (stateless, JSON
            responses). All requests are <Mono>POST</Mono> and must be authenticated. An
            unauthenticated request is rejected with <Mono>401</Mono> before any tool is exposed.
          </P>
          <CodeBlock title="Endpoint">{`POST https://api.logpulse.io/mcp`}</CodeBlock>
          <P>
            The endpoint and an authentication header are everything an MCP client needs; the client
            discovers the available tools, resources, and prompts automatically after it connects.
          </P>
        </Section>

        <Section id="authentication" title="Authentication">
          <P>
            Two authentication methods resolve to the same identity (organization, user, scopes): a
            Personal Access Token as a bearer credential, or OAuth 2.1 for interactive sign-in.
          </P>

          <SubSection id="auth-pat" title="Personal access token">
            <P>
              Create a token in{' '}
              <a
                href="https://app.logpulse.io/profile?tab=tokens"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Profile → Access Tokens
              </a>
              , grant it the scopes the agent needs, and send it as a bearer token. A token is tied to
              you and one organization and never grants more than your own access.
            </P>
            <CodeBlock title="Authorization header">
{`Authorization: Bearer lpat_a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4`}
            </CodeBlock>
          </SubSection>

          <SubSection id="auth-oauth" title="OAuth 2.1">
            <P>
              Interactive clients can connect with OAuth 2.1 (PKCE, dynamic client registration, and
              browser consent on the LogPulse scopes), so a user authorizes the agent without
              handling a token by hand. The agent registers itself, the user signs in and consents,
              and LogPulse issues a scoped access token bound to the agent and organization. Connected
              agents are listed, and can be revoked, under{' '}
              <a
                href="https://app.logpulse.io/profile?tab=tokens"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Profile → Access Tokens
              </a>
              .
            </P>
            <Callout type="tip">
              Use a PAT for CI and headless automation; use OAuth for interactive, per-user
              connections from a developer's editor.
            </Callout>
          </SubSection>

          <SubSection id="auth-organizations" title="Several organizations">
            <P>
              One token or connection can reach several organizations. Pick them when you create the
              token (Profile → Access Tokens) or on the OAuth consent screen; only organizations you
              belong to can be chosen, and membership is checked again on every request.
            </P>
            <P>
              Such a connection has no current organization. <Mono>list_organizations</Mono> shows
              the choices, and every other tool takes a required <Mono>organization</Mono> argument
              (id, slug or exact name). The agent is instructed to ask you when it is not sure which
              organization you mean. A call without one, or with a name that matches more than one
              organization, runs nothing and returns the list. Plan, role, data access and the audit
              trail are those of the organization the call names.
            </P>
          </SubSection>
        </Section>

        <Section id="scopes" title="Scopes">
          <P>
            Scopes follow a <Mono>resource:action</Mono> convention. A token only sees the tools its
            scopes allow. A tool the token can't reach is invisible in <Mono>tools/list</Mono> and
            cannot be called. Follow least privilege and grant only what the agent needs.
          </P>
          <Table
            headers={['Scope', 'Grants']}
            rows={[
              [<Mono>logs:read</Mono>, 'Log search and investigation, plus control-plane reads (alerts, pipelines, dashboards, agents, saved queries) and the onboarding data model'],
              [<Mono>siem:read</Mono>, 'Risk events, risk summary, notables, detections, UEBA baselines, MITRE coverage, and IOC lookups'],
              [<Mono>services:read</Mono>, 'Service health, KPI anomalies, dependencies, services, and incidents'],
              [<Mono>entities:read</Mono>, 'Entity 360, blast radius, and entity risk timeline (cross-domain graph)'],
              [<Mono>siem:propose</Mono>, 'Propose detection rules and alert rules for human review (queued, not applied)'],
              [<Mono>services:propose</Mono>, 'Propose services, KPIs, dependencies, SLOs and notification routes (Service Intelligence) for human review'],
              [<Mono>pipelines:propose</Mono>, 'Propose SOAR / automation pipelines and triggers for human review'],
              [<Mono>parsers:propose</Mono>, 'Propose parser rules (field extraction at ingest), proven on real events first'],
              [<Mono>dashboards:propose</Mono>, 'Propose dashboards and panels; every panel search runs once first. Never shared on a public link'],
              [<Mono>checks:propose</Mono>, 'Propose health checks; each check is run once from LogPulse first. Needs a role that may change settings'],
              [<Mono>ops:act</Mono>, 'Widget actions you click yourself in ChatGPT or Claude: acknowledge or close a notable, mute a rule for a few hours, snooze an anomaly, undo. The AI model cannot call these'],
              [<Mono>proposals:decide</Mono>, 'Approve or reject a proposal from a widget, confirmed by you in LogPulse each time; never your own proposals and never by the AI. Opt-in on the consent screen'],
            ]}
          />
          <Callout type="warning">
            There are no direct-write scopes. The <Mono>*:propose</Mono> scopes let an agent only{' '}
            <em>propose</em> a change. It is queued in LogPulse and a human with the right permission
            approves it before anything is applied. Approved items are created disabled/inactive, and
            destructive SOAR actions require owner approval. Grant the narrowest propose scope an agent
            needs.
          </Callout>
        </Section>

        <Section id="connecting" title="Connecting an agent">
          <P>
            Any MCP client that supports remote servers over HTTP connects to the same endpoint.
            ChatGPT and Claude sign in with OAuth; coding agents can use OAuth or a bearer token.
          </P>

          <SubSection id="connect-chatgpt" title="ChatGPT">
            <P>
              Add LogPulse as an app in ChatGPT: from the app directory when it is listed for your
              account, or as a custom app in developer mode (Settings → Apps → Advanced settings) with
              this URL and OAuth authentication:
            </P>
            <CodeBlock title="MCP server URL">{`https://api.logpulse.io/mcp`}</CodeBlock>
            <P>
              ChatGPT sends you to LogPulse to sign in. On the consent screen you pick the
              organizations ChatGPT may use and, under Change access, trim the permissions. Results
              from searches, notables, service health, dashboards and proposals render as interactive
              widgets in the chat.
            </P>
            <Callout type="tip">
              Added LogPulse a while ago and a new tool or widget is missing? ChatGPT keeps the tool
              list from when the app was added. Refresh the app in its settings, or remove and add it
              again.
            </Callout>
          </SubSection>

          <SubSection id="connect-claude-ai" title="Claude (web, desktop, mobile)">
            <P>
              Add LogPulse from the Claude connectors directory when it is listed there (Settings →
              Connectors → Browse), or as a custom connector with this URL:
            </P>
            <CodeBlock title="Remote MCP server URL">{`https://api.logpulse.io/mcp`}</CodeBlock>
            <P>
              Claude opens LogPulse to sign in and shows the same consent screen. Once connected,
              LogPulse works in every conversation where the connector is enabled, on the web, in the
              desktop app and on mobile, with the same interactive widgets.
            </P>
          </SubSection>

          <SubSection id="connect-claude" title="Claude Code">
            <P>
              Add LogPulse with the Claude CLI. Without a header, Claude Code signs in with OAuth the
              first time you use it (run <Mono>/mcp</Mono> to authenticate); with a token it connects
              directly:
            </P>
            <CodeBlock title="Terminal">
{`claude mcp add logpulse --transport http \\
  https://api.logpulse.io/mcp \\
  --header "Authorization: Bearer lpat_your_token_here"`}
            </CodeBlock>
            <P>
              The LogPulse tools appear immediately in any Claude Code session. Verify with{' '}
              <Mono>/mcp</Mono> or by asking Claude to list its LogPulse tools.
            </P>
          </SubSection>

          <SubSection id="connect-cursor" title="Cursor">
            <P>
              Add LogPulse as an HTTP MCP server in Cursor's MCP settings, or edit{' '}
              <Mono>~/.cursor/mcp.json</Mono> directly:
            </P>
            <CodeBlock title="~/.cursor/mcp.json">
{`{
  "mcpServers": {
    "logpulse": {
      "url": "https://api.logpulse.io/mcp",
      "headers": {
        "Authorization": "Bearer lpat_your_token_here"
      }
    }
  }
}`}
            </CodeBlock>
          </SubSection>

          <SubSection id="connect-codex" title="Codex & other clients">
            <P>
              Codex, Continue, and custom agents all connect with the same three values. Point your
              client at the endpoint over Streamable HTTP with a bearer token:
            </P>
            <CodeBlock title="Connection values">
{`Endpoint:  https://api.logpulse.io/mcp
Transport: Streamable HTTP
Auth:      Authorization: Bearer lpat_your_token_here`}
            </CodeBlock>
          </SubSection>

          <SubSection id="reconnect" title="Reconnecting an agent">
            <P>
              LogPulse moved MCP sign-in to a new OAuth server in late 2026. Agents connected with
              OAuth before that upgrade carry over by themselves the next time they refresh their
              sign-in, and an agent that asks you to sign in again keeps its connection once you
              approve it. Agents that use a personal access token are not affected. If an agent
              still fails with <Mono>401</Mono>, connect it again:
            </P>
            <ol className="mb-4 list-decimal space-y-2 pl-6 text-gray-600 dark:text-gray-400">
              <li>
                Remove LogPulse from the agent: in Claude, ChatGPT or Cursor delete the connector or
                app; in Claude Code run <Mono>claude mcp remove logpulse</Mono>.
              </li>
              <li>
                Add it again with <Mono>https://api.logpulse.io/mcp</Mono>, as described above for
                your agent.
              </li>
              <li>
                Sign in to LogPulse and approve the connection on the consent screen, with the
                organizations and permissions it may use.
              </li>
            </ol>
            <P>
              The new connection shows under{' '}
              <a
                href="https://app.logpulse.io/profile?tab=tokens"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Profile → Access Tokens
              </a>
.
            </P>
          </SubSection>
        </Section>

        <Section id="example-prompts" title="Example prompts">
          <P>
            Ask in your own words; the assistant picks the tools. With several organizations
            connected, name the one you mean.
          </P>
          <Table
            headers={['Ask', 'What happens']}
            rows={[
              ['What happened in production overnight?', 'A briefing: new notables, anomalies, failing health checks and open incidents since your last look.'],
              ['Show the 5xx rate for the checkout service over the last 24 hours.', 'An LPQL search with a timechart widget; the query is shown so you can reuse it.'],
              ['Which notables are open, and which ones look like false positives?', 'The notable queue with risk scores; you can acknowledge, close or mark false positives from the widget.'],
              ['Is the payments service healthy, and what does it depend on?', 'Service health per KPI and the dependency map, with the KPI that is out of bounds.'],
              ['Propose an alert when login failures for one user exceed 20 in 5 minutes.', 'A proposal tested against your real data (a dry run with the matches it would have produced), queued for a person to approve.'],
            ]}
          />
        </Section>

        <Section id="tools" title="Tool catalog">
          <P>
            Tools are grouped by the scope that unlocks them. The catalog mirrors the depth your team
            uses in the dashboard.
          </P>

          <SubSection id="tools-logs" title="Search & investigate (logs:read)">
            <Table
              headers={['Tool', 'Description']}
              rows={[
                [<Mono>search_logs</Mono>, 'Run an LPQL search and return matching events'],
                [<Mono>count_patterns</Mono>, 'Group and count events by a field or pattern'],
                [<Mono>timeline_analysis</Mono>, 'Bucket events over time to spot spikes and gaps'],
                [<Mono>compare_timeranges</Mono>, 'Compare two time windows for the same query'],
                [<Mono>get_field_values</Mono>, 'List the distinct values of a field'],
                [<Mono>search_similar_historical</Mono>, 'Find historical events similar to a reference'],
                [<Mono>system_health</Mono>, 'Snapshot of ingest and platform health'],
                [<Mono>lpql_help</Mono>, 'LPQL reference for building queries'],
                [<Mono>list_* / get_*</Mono>, 'Control-plane reads: alerts, pipelines, dashboards, agents, lookups, saved queries, evaluations, maintenance windows'],
                [<Mono>get_data_model</Mono>, 'Canonical / OCSF field model for building connectors'],
                [<Mono>estimate_search_cost</Mono>, 'Rows a search reads per run and per day, without running it, and how to make it cheaper'],
                [<Mono>find_unparsed_sources</Mono>, 'The index and sourcetype pairs with the most events without extracted fields, with an example event'],
                [<Mono>check_parsing / test_parser_rule</Mono>, 'How a source is parsed today, and a candidate parser rule tested on real events (with a search that backfills older events)'],
              ]}
            />
          </SubSection>

          <SubSection id="tools-siem" title="Security monitoring (siem:read)">
            <Table
              headers={['Tool', 'Description']}
              rows={[
                [<Mono>search_risk_events</Mono>, 'Search the stream of risk events by entity, MITRE tag, score, or time'],
                [<Mono>get_risk_summary</Mono>, 'Top-risk-entity leaderboard and risk breakdown'],
                [<Mono>list_notables / get_notable</Mono>, 'List and inspect notables (with investigation state)'],
                [<Mono>list_detections / get_detection</Mono>, 'Browse and read detection rules'],
                [<Mono>get_ueba_baselines</Mono>, 'Behavioral baselines for an entity'],
                [<Mono>mitre_coverage</Mono>, 'MITRE ATT&CK coverage from enabled content'],
                [<Mono>lookup_ioc</Mono>, 'IOC reputation lookup against threat-intel feeds'],
                [<Mono>get_siem_settings</Mono>, 'Read the risk-scoring configuration'],
              ]}
            />
          </SubSection>

          <SubSection id="tools-services" title="Service intelligence (services:read)">
            <Table
              headers={['Tool', 'Description']}
              rows={[
                [<Mono>get_service_health</Mono>, 'Service status rolled up from its KPIs'],
                [<Mono>list_slos</Mono>, 'SLOs with target, SLI, error budget left, burn rates and status'],
                [<Mono>list_anomalies</Mono>, 'KPI anomalies across services'],
                [<Mono>list_dependencies</Mono>, 'Upstream/downstream dependency graph'],
                [<Mono>list_services</Mono>, 'Services in the estate'],
                [<Mono>list_incidents</Mono>, 'Open and recent incidents'],
                [<Mono>get_service</Mono>, 'One service in full: scope, membership rules, every KPI, dependencies in both directions'],
                [<Mono>list_kpi_templates</Mono>, 'Golden-signal KPI templates to start from'],
                [<Mono>suggest_services</Mono>, 'Services discovery finds in your data that do not exist yet, with ready-made rules'],
                [<Mono>suggest_dependencies</Mono>, 'Dependencies the observed traffic between services suggests'],
                [<Mono>preview_service_scope</Mono>, 'Which entities or sources a set of membership rules matches right now'],
                [<Mono>test_kpi</Mono>, 'Run a KPI once inside a service scope: the value it would record and its severity (also needs logs:read)'],
              ]}
            />
          </SubSection>

          <SubSection id="tools-entities" title="Entity graph (entities:read)">
            <Table
              headers={['Tool', 'Description']}
              rows={[
                [<Mono>get_entity</Mono>, 'Entity 360 across security and observability'],
                [<Mono>get_blast_radius</Mono>, 'What an entity connects to and could affect'],
                [<Mono>get_entity_risk_timeline</Mono>, "An entity's risk over time"],
              ]}
            />
          </SubSection>

          <SubSection id="tools-write" title="Propose changes (propose-approve)">
            <P>
              Agents never write configuration directly. Each <Mono>propose_*</Mono> tool queues a
              change in LogPulse for a human to review and approve; approved items are created
              disabled/inactive for a person to activate. Destructive SOAR actions
              (<Mono>block_ip</Mono>, <Mono>disable_user</Mono>) require owner approval plus the org's
              destructive-actions flag, and agent-side HTTP nodes are rejected.
            </P>
            <Table
              headers={['Tool', 'Scope', 'Description']}
              rows={[
                [<Mono>propose_detection</Mono>, <Mono>siem:propose</Mono>, 'Propose a detection rule (created disabled on approval)'],
                [<Mono>propose_alert_rule</Mono>, <Mono>siem:propose</Mono>, 'Propose a search-based alert rule (notifications added by a human)'],
                [<Mono>propose_service</Mono>, <Mono>services:propose</Mono>, 'Propose a Service Intelligence service'],
                [<Mono>propose_kpi</Mono>, <Mono>services:propose</Mono>, 'Propose a KPI on a service'],
                [<Mono>propose_service_setup</Mono>, <Mono>services:propose</Mono>, 'A whole service in one change: service, KPIs and dependencies, applied all or nothing'],
                [<Mono>propose_service_update</Mono>, <Mono>services:propose</Mono>, 'Change a service; the reviewer sees each field before and after'],
                [<Mono>propose_service_delete</Mono>, <Mono>services:propose</Mono>, 'Delete a service, listing the KPIs and dependencies that go with it'],
                [<Mono>propose_kpi_update</Mono>, <Mono>services:propose</Mono>, 'Change a KPI; a new search runs once against your data first'],
                [<Mono>propose_kpi_delete</Mono>, <Mono>services:propose</Mono>, 'Delete a KPI'],
                [<Mono>propose_dependency</Mono>, <Mono>services:propose</Mono>, 'Add, change or remove a dependency between two services'],
                [<Mono>list_my_proposals</Mono>, <Mono>services:propose</Mono>, 'What this user proposed and where each proposal stands'],
                [<Mono>propose_parser_rule</Mono>, <Mono>parsers:propose</Mono>, 'Create, change or delete a parser rule; refused unless it parses the real events it is for'],
                [<Mono>propose_slo</Mono>, <Mono>services:propose</Mono>, 'Create, change or delete an SLO; measured over the last 7 days first, refused when that week already spent the whole error budget'],
                [<Mono>list_notification_channels</Mono>, <Mono>services:propose</Mono>, 'The notification channels that exist, with masked targets (never a URL, token or address list)'],
                [<Mono>propose_service_notification</Mono>, <Mono>services:propose</Mono>, 'Route the alerts of a service to an existing channel, change or remove a route. Changes who gets paged; cannot add a recipient'],
                [<Mono>propose_dashboard</Mono>, <Mono>dashboards:propose</Mono>, 'Create a dashboard with its panels, change its name, visibility or layout, or delete it'],
                [<Mono>propose_dashboard_panel</Mono>, <Mono>dashboards:propose</Mono>, 'Add, change or remove one panel; its search runs once over the last 15 minutes first'],
                [<Mono>propose_health_check_change</Mono>, <Mono>checks:propose</Mono>, 'Create, change or delete a health check; run once from LogPulse first, private and internal addresses refused'],
                [<Mono>propose_pipeline</Mono>, <Mono>pipelines:propose</Mono>, 'Propose a SOAR / automation pipeline (created inactive)'],
                [<Mono>propose_automation_trigger</Mono>, <Mono>pipelines:propose</Mono>, 'Propose a notable → pipeline trigger (created disabled)'],
              ]}
            />
            <P>
              Service Intelligence proposals are checked against your data before they are queued:
              the membership rules must match entities or sources, every KPI search runs once inside
              the service scope, and a dependency must point at an existing service. What the check
              found travels with the proposal, so the reviewer sees the same evidence. An update or
              delete that was overtaken by a manual edit fails instead of overwriting it.
            </P>
            <P>
              <strong className="text-gray-900 dark:text-white">Changes without a review.</strong>{' '}
              By default every proposal waits for a person. An owner or admin can let the agent apply
              a category by itself under AI → Settings (services, KPIs, dependencies, SLOs,
              notification routes, dashboards, health checks, parser rules, detections, alert rules,
              pipelines), each off until switched on. An applied change is still
              recorded as a proposal with its diff, runs under the rights of the token's owner, and
              lands in the audit log. Destructive changes always wait for the owner.
            </P>
            <P>
              <strong className="text-gray-900 dark:text-white">Parser rules.</strong>{' '}
              <Mono>check_parsing</Mono> shows how a source is parsed today, <Mono>list_grok_templates</Mono>{' '}
              the building blocks, and <Mono>test_parser_rule</Mono> runs a candidate over real events
              in the same engine ingest uses (all under <Mono>logs:read</Mono>). A proposed rule is
              tested again on the events its source pattern selects and refused when fewer than 80%
              parse, when the pattern is unsafe or too slow, or when another rule would win for the
              same events. A rule applies to events that arrive after it is live; for regex and grok
              rules the result carries a <Mono>backfillSearch</Mono>, an LPQL search with{' '}
              <Mono>| rex</Mono> that extracts the same fields from events already stored.{' '}
              <Mono>find_unparsed_sources</Mono> shows where a rule would help most.
            </P>
            <P>
              <strong className="text-gray-900 dark:text-white">Scheduled searches show their cost.</strong>{' '}
              A detection, alert rule or KPI runs forever, so before it is queued the server estimates
              the rows it reads per day (the store's own estimate, or the hourly counts) and shows that
              number to the reviewer, with advice on how to narrow it. Nothing is refused on cost.
            </P>
            <P>
              <strong className="text-gray-900 dark:text-white">SLOs, notification routes, dashboards and health checks.</strong>{' '}
              A proposed SLO is measured over the last 7 days with the computation the SLO editor's
              preview uses, and the reviewer sees its SLI, error budget and burn rate. A notification
              route names a channel that already exists by its id: an agent cannot add a recipient,
              an email address or a webhook URL, and it only ever sees masked targets. Every panel
              search of a proposed dashboard runs once and must be narrowed. A proposed health check
              is run once from LogPulse behind the same protection against private and internal
              addresses as the Test button; a check that runs on an agent needs an online agent.
            </P>
            <P>
              <strong className="text-gray-900 dark:text-white">Searches must be narrowed.</strong>{' '}
              The log store is sorted by index, so a proposed detection or alert rule without{' '}
              <Mono>index=</Mono>, <Mono>sourcetype=</Mono>, <Mono>source=</Mono> or <Mono>host=</Mono>{' '}
              before its first pipe is refused; a KPI runs inside its service scope and is advised to
              add <Mono>index=</Mono>. <Mono>platform_help</Mono> teaches the agent how to build an
              efficient search and what can and cannot be done over MCP, and{' '}
              <Mono>get_agent_permissions</Mono> tells it, per kind of change, whether a proposal is
              applied at once or waits for a person. Both need no scope.
            </P>
            <Callout type="info">
              <Mono>detection_help</Mono> (under <Mono>logs:read</Mono>) explains how detections and
              KPIs map onto the data model, so an agent can draft a correct rule before calling{' '}
              <Mono>propose_detection</Mono>.
            </Callout>
          </SubSection>
        </Section>

        <Section id="resources-prompts" title="Resources & prompts">
          <P>
            Beyond callable tools, the server exposes MCP resources for read-grounding and prompts
            that steer the agent through a workflow.
          </P>
          <P>
            <strong className="text-gray-900 dark:text-white">Resources</strong> include an LPQL cheat
            sheet and a detection guide (available with any valid token), plus your data models and
            saved queries (gated by <Mono>logs:read</Mono>). A resource the token can't reach is
            hidden from <Mono>resources/list</Mono>.
          </P>
          <P>
            <strong className="text-gray-900 dark:text-white">Prompts</strong> include{' '}
            <Mono>triage_notable</Mono>, <Mono>is_ip_malicious</Mono>, <Mono>onboard_connector</Mono>,
            <Mono>service_health_check</Mono> and <Mono>set_up_service</Mono>, each guides the agent through the right sequence of
            read-only tools for that task.
          </P>
        </Section>

        <Section id="security" title="Security model">
          <P>The MCP surface is governed by design:</P>
          <P>
            <strong className="text-gray-900 dark:text-white">The AI never writes directly.</strong>{' '}
            Every configuration change goes through propose-approve: a{' '}
            <Mono>propose_*</Mono> tool (behind a <Mono>*:propose</Mono> scope) queues a change that a
            human approves in LogPulse before anything is applied, unless an owner has chosen to let
            the AI apply proposals in AI → Settings. The few direct actions (acknowledge a notable,
            mute a rule, undo) are hidden from the model and only run when you click them in a widget.
          </P>
          <P>
            <strong className="text-gray-900 dark:text-white">Scoped & RBAC-aware.</strong> A token
            holds only the scopes you grant, and the token holder's namespace RBAC is applied to every
            query. An agent can never read across tenants or beyond what the user's roles grant.
          </P>
          <P>
            <strong className="text-gray-900 dark:text-white">Audited & rate-limited.</strong> Every
            tool call is written to the audit log (denied calls included) and rate-limited per token.
          </P>
          <P>
            <strong className="text-gray-900 dark:text-white">Usage is a detection.</strong> Each call
            is also emitted to an internal log stream and watched by built-in detections. Abnormal
            call volume or an agent write raises a notable on your own engine.
          </P>
          <P>
            <strong className="text-gray-900 dark:text-white">Untrusted data stays data.</strong> Tool
            output is treated as data, not instructions, as a guard against prompt injection through
            your own logs. There is no auto-execute and no write without a human in the loop. Data
            stays EU-hosted and AI evaluation runs in the EU.
          </P>
        </Section>

        <Section id="data-privacy" title="Data & privacy">
          <P>
            The MCP server only returns data from the organizations you selected on the consent
            screen, within your role and the scopes you granted. LogPulse does not read your
            conversations with ChatGPT or Claude: it receives the tool calls the assistant makes and
            records each one (tool, organization, user, time) in the audit log. What the assistant does
            with a result is governed by the assistant's own terms. LogPulse data is hosted in the EU.
            See the{' '}
            <Link to="/privacy" className="text-primary-600 dark:text-primary-400 hover:underline">
              privacy policy
            </Link>{' '}
            for retention, processors and your rights. Revoke a connection at any time under Profile →
            Access Tokens; its tokens stop working immediately.
          </P>
        </Section>

        <Section id="limits" title="Rate limits & auditing">
          <P>
            The endpoint is rate-limited to <Mono>120 requests/min per token</Mono>. Exceeding it
            returns <Mono>429</Mono>; back off and retry. Each successful call updates the token's
            "last used" timestamp in Settings, and every call is recorded in the organization's audit
            log with the user, scope, and whether it was a write.
          </P>
          
          <P>
            On top of the rate limit, an owner or admin can give each connection (an access token or
            an OAuth client) a daily limit per organization on tool calls and on rows read, under AI →
            Usage. There is none by default. Over a limit, a tool call returns an error that says which
            limit was reached and when it resets: 00:00 UTC. The same page shows calls and rows read per
            day and per connection.
          </P>
          <Callout type="info">
            MCP access is part of your LogPulse plan and governed by the per-token rate limit. Pricing
            stays flat, billed per plan, not per query or per agent.
          </Callout>
        </Section>

        <Section id="troubleshooting" title="Troubleshooting">
          <Table
            headers={['Symptom', 'Cause & fix']}
            rows={[
              ['401 on connect', 'Missing or invalid token. Confirm the Authorization header is Bearer lpat_… and the token is not revoked or expired.'],
              ['401 for an agent that used to work over OAuth', 'It was connected before the late-2026 sign-in upgrade and did not carry over. Sign in again from the agent, or remove LogPulse from it and add it again (see Reconnecting an agent).'],
              ['A tool is missing from the list', 'The token lacks that tool’s scope. Create a token with the right scope (for example siem:read for notables).'],
              ['403 calling a tool', 'The token is valid but missing the required scope for that specific tool.'],
              ['429 responses', 'Per-token rate limit (120/min) exceeded. Reduce call volume or back off and retry.'],
              ['"Daily limit reached"', 'An admin set a daily limit for connections in this organization and this one reached it. It resets at 00:00 UTC; the limit is changed under AI → Usage.'],
              ['A proposed change is not live yet', 'Expected: propose_* tools queue a change for human approval. Approve it in LogPulse; approved items are created disabled/inactive, then a person enables them.'],
            ]}
          />
          <P>
            Still stuck? See{' '}
            <Link to="/docs/api/personal-access-tokens" className="text-primary-600 dark:text-primary-400 hover:underline">
              Personal Access Tokens
            </Link>{' '}
            for token errors, or{' '}
            <Link to="/contact" className="text-primary-600 dark:text-primary-400 hover:underline">
              contact us
            </Link>
            .
          </P>
        </Section>
      </div>

      <TableOfContents items={toc} />
    </div>
  );
}
